'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAppStore } from '@entities/app-store'
import { fulfillmentRequestApi } from '@entities/fulfillment-request'
import { faFileLines, faInbox, faPlus, faWarehouse } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'
import getApiResponseData from '@utils/get-api-response-data'
import { joinPagePath } from '@widgets/fulfillment-join'
import { requestLandingPath, requestWizardPath } from '@widgets/fulfillment-request-landing'
import { observer } from 'mobx-react-lite'

const catalogLink = { href: '/fulfillment', label: 'Каталог ФФ', icon: faWarehouse }
const inboxHref = '/fulfillment/requests/inbox'
const addHref = '/fulfillment/add'
const userLinks = [
  { href: '/fulfillment/my', label: 'Мои фулфилменты', icon: faWarehouse },
  { href: '/fulfillment/requests', label: 'Мои заявки', icon: faFileLines },
  { href: inboxHref, label: 'Заявки селлеров на просчёт', icon: faInbox },
]

/**
 * Открыт самый длинный подходящий пункт: страница заявок селлеров лежит внутри «Моих заявок»,
 * и без выбора по длине подсвечивались бы оба.
 */
const currentHref = (pathname: string, hrefs: string[]) =>
  hrefs
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((left, right) => right.length - left.length)[0]

/**
 * Сколько заявок ждёт ответа. Считает отдельный лёгкий запрос: сами заявки грузятся только
 * на своей странице, а число нужно в меню на всех страницах раздела.
 */
const useNewRequestCount = (isLoggedIn: boolean) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    let isActual = true
    fulfillmentRequestApi
      .inboxCount()
      .then(({ data }) => {
        if (isActual) {
          setCount(getApiResponseData(data)?.new ?? 0)
        }
      })
      .catch(() => undefined)

    return () => {
      isActual = false
    }
  }, [isLoggedIn])

  return count
}

/** Постоянная панель раздела: куда вернуться и куда перейти, видна на каждой странице фулфилментов. */
export const FulfillmentSectionNav = observer(() => {
  const pathname = usePathname()
  const appStore = useAppStore()
  const newRequestCount = useNewRequestCount(appStore.isLoggedIn)
  const links = appStore.isLoggedIn ? [catalogLink, ...userLinks] : [catalogLink]
  const current = currentHref(
    pathname,
    links.map((link) => link.href)
  )

  return (
    <div className="border-b">
      <div className="mx-auto flex w-full max-w-[1520px] flex-wrap items-center gap-x-1 gap-y-2 px-4 py-2 sm:px-6 lg:px-8">
        {links.map((link) => (
          <Button
            key={link.href}
            size="sm"
            variant={link.href === current ? 'secondary' : 'ghost'}
            render={<Link href={link.href} />}
          >
            <FontAwesomeIcon icon={link.icon} />
            {link.label}
            {link.href === inboxHref && newRequestCount > 0 && (
              <Badge variant="destructive" aria-label={`новых заявок: ${newRequestCount}`}>
                {newRequestCount}
              </Badge>
            )}
          </Button>
        ))}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {pathname !== requestWizardPath && pathname !== requestLandingPath && (
            // Гостю бланк формы ничего не объясняет, поэтому его ведём на страницу-объяснение:
            // там написано, кому уйдёт заявка и что будет дальше, и оттуда он переходит в ту же форму.
            <Button
              size="sm"
              variant="warning"
              render={<Link href={appStore.isLoggedIn ? requestWizardPath : requestLandingPath} />}
            >
              <FontAwesomeIcon icon={faFileLines} />
              Заявка на просчёт
            </Button>
          )}
          {pathname !== addHref && pathname !== joinPagePath && (
            // Гостю бланк формы ничего не объясняет, поэтому его ведём на страницу-приглашение:
            // там написано, зачем размещаться, и оттуда он переходит в ту же форму.
            <Button size="sm" render={<Link href={appStore.isLoggedIn ? addHref : joinPagePath} />}>
              <FontAwesomeIcon icon={faPlus} />
              Добавить свой ФФ
            </Button>
          )}
        </span>
      </div>
    </div>
  )
})
